import type { PlayerStats } from "../game/stats";

// Meta-progression: Fragments earned in runs bank permanently and are spent in
// the Upgrade Lab on permanent buffs to your starting stats. Persisted locally.
export interface MetaUpgrade {
  id: string;
  name: string;
  desc: string;
  icon: string; // vector icon key (see ui/icons.ts)
  maxLevel: number;
  baseCost: number; // cost of level 1; each level costs baseCost*(level+1)
  apply: (stats: PlayerStats, level: number) => void;
}

export const META_UPGRADES: MetaUpgrade[] = [
  {
    id: "overclock",
    icon: "bolt",
    name: "OVERCLOCK",
    desc: "+15% damage / lvl",
    maxLevel: 8,
    baseCost: 50,
    apply: (s, l) => (s.damageMult += 0.15 * l),
  },
  {
    id: "rapid_fire",
    icon: "chevrons",
    name: "RAPID FIRE",
    desc: "+10% attack speed / lvl",
    maxLevel: 6,
    baseCost: 60,
    apply: (s, l) => (s.attackSpeed += 0.1 * l),
  },
  {
    id: "overcharge",
    icon: "split",
    name: "OVERCHARGE",
    desc: "+1 projectile / target per lvl",
    maxLevel: 3,
    baseCost: 150,
    apply: (s, l) => (s.countAdd += l),
  },
  {
    id: "reinforced_core",
    icon: "shield",
    name: "REINFORCED CORE",
    desc: "+25 max integrity / lvl",
    maxLevel: 6,
    baseCost: 45,
    apply: (s, l) => (s.maxIntegrityAdd += 25 * l),
  },
  {
    id: "routing",
    icon: "boots",
    name: "AUTONOMOUS ROUTING",
    desc: "+8% move speed / lvl",
    maxLevel: 5,
    baseCost: 50,
    apply: (s, l) => (s.moveSpeedMult += 0.08 * l),
  },
  {
    id: "magnet",
    icon: "magnet",
    name: "MAGNET ARRAY",
    desc: "+30% pickup radius / lvl",
    maxLevel: 5,
    baseCost: 40,
    apply: (s, l) => (s.pickupRadiusMult += 0.3 * l),
  },
  {
    id: "prediction",
    icon: "target",
    name: "PREDICTION ENGINE",
    desc: "+6% crit chance / lvl",
    maxLevel: 6,
    baseCost: 55,
    apply: (s, l) => (s.critChanceAdd += 0.06 * l),
  },
  {
    id: "repair",
    icon: "cross",
    name: "REPAIR PROTOCOL",
    desc: "+0.6 integrity/sec / lvl",
    maxLevel: 5,
    baseCost: 60,
    apply: (s, l) => (s.regenPerSec += 0.6 * l),
  },
];

export interface MetaState {
  fragments: number; // banked, spendable
  bestZone: number;
  upgrades: Record<string, number>; // id -> purchased level
}

const KEY = "pckt_meta";

export function loadMeta(): MetaState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const m = JSON.parse(raw) as Partial<MetaState>;
      return {
        fragments: Math.max(0, Math.floor(Number(m.fragments) || 0)),
        bestZone: Math.max(0, Math.floor(Number(m.bestZone) || 0)),
        upgrades: typeof m.upgrades === "object" && m.upgrades ? m.upgrades : {},
      };
    }
  } catch {
    /* ignore */
  }
  return { fragments: 0, bestZone: 0, upgrades: {} };
}

export function saveMeta(m: MetaState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

export function metaLevel(m: MetaState, id: string): number {
  return m.upgrades[id] ?? 0;
}

/** Fragments to buy the NEXT level of an upgrade, or null if maxed. */
export function metaNextCost(m: MetaState, u: MetaUpgrade): number | null {
  const lvl = metaLevel(m, u.id);
  if (lvl >= u.maxLevel) return null;
  return u.baseCost * (lvl + 1);
}

/** Buy one level if affordable. Mutates+saves and returns true on success. */
export function buyMeta(m: MetaState, u: MetaUpgrade): boolean {
  const cost = metaNextCost(m, u);
  if (cost === null || m.fragments < cost) return false;
  m.fragments -= cost;
  m.upgrades[u.id] = metaLevel(m, u.id) + 1;
  saveMeta(m);
  return true;
}

/** Bank a finished run's fragments + best zone. */
export function bankRun(m: MetaState, fragments: number, zone: number): void {
  m.fragments += Math.max(0, Math.floor(fragments));
  m.bestZone = Math.max(m.bestZone, zone);
  saveMeta(m);
}

/** Apply all purchased meta-upgrades to a fresh run's stats. */
export function applyMeta(stats: PlayerStats, m: MetaState): void {
  for (const u of META_UPGRADES) {
    const lvl = metaLevel(m, u.id);
    if (lvl > 0) u.apply(stats, lvl);
  }
}
